import { Injectable } from "@angular/core";
import { FilterContactsDto } from "./dtos/filter-contacts.dto";
import {
  BehaviorSubject,
  catchError,
  debounce,
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
  throwError,
} from "rxjs";
import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { GetFilterdContactsResponseDTO } from "./models/contact.model";
import { environment } from "src/environments/environment";
import { CreateContactListDto } from "./dtos/create-contact-list.dto";
import { GetContactListResponseDTO } from "./dtos/get-contact-list-response.dto";
import { GetContactListRequestDTO } from "./dtos/contact-list-request.dto";
import { PaginationDto } from "src/app/core/dto/pagination.dto";
import { GetDetailedListRequestDTO } from "./dtos/detailed-list-request.dto";
import { DetailedListModel } from "./models/detailed-list.model";
import { DetailedListClient } from "./models/detailed-list-client.model";
import { GetQualifiedContactListRequestDTO } from "./dtos/qualified-contact-list-request.dto";
import { GetQualifiedContactListResponseDTO } from "./dtos/qualified-contact-list-response.dto";
import { CountValidEmailsResponseDTO } from "./dtos/count-valid-emails-response.dto";
@Injectable({
  providedIn: "root",
})
export class ContactService {
  private _apiUrl = environment.apiUrl + "/v1/ContatosGerados";
  private filtersSubject = new BehaviorSubject<FilterContactsDto>({
    "Paginacao.PaginaAtual": "1",
    "Paginacao.QuantidadePorPagina": "10",
    FeiraId: "",
    DataAgendamentoAte: "",
    DataAgendamentoDe: "",
    MetroQuadradoAte: "",
    MetroQuadradoDe: "",
    StatusId: "",
  });
  private loadingSubject = new BehaviorSubject<boolean>(true);

  constructor(private http: HttpClient) { }

  setFilters(filters: Partial<FilterContactsDto>): void {
    const currentFilters = this.filtersSubject.value;
    this.filtersSubject.next({ ...currentFilters, ...filters });
  }

  get filters(): FilterContactsDto {
    return this.filtersSubject.value;
  }

  get loading() {
    return this.loadingSubject.asObservable();
  }

  getContactFilters() {
    return this.filtersSubject.pipe(
      debounceTime(300), // Debounce to avoid unnecessary requests
      distinctUntilChanged(),
      switchMap((filters) => {
        let params = new HttpParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value) {
            params = params.append(key, value.toString());
          }
        });
        this.loadingSubject.next(true);
        return this.http
          .get<GetFilterdContactsResponseDTO>(
            `${this._apiUrl}/filtrar-lista-gerar-contatos`,
            { params },
          )
          .pipe(
            finalize(() => {
              this.loadingSubject.next(false);
            }),
          );
      }),
    );
  }
  createContactList(name: string) {
    const data: CreateContactListDto = {
      descricaoLista: name,
      dataAgendamentoAte: this.filters.DataAgendamentoAte || null,
      dataAgendamentoDe: this.filters.DataAgendamentoDe || null,
      feiraId: this.filters.FeiraId,
      metroQuadradoAte: this.filters.MetroQuadradoAte || null,
      metroQuadradoDe: this.filters.MetroQuadradoDe || null,
      statusId: this.filters.StatusId || null,
    };
    return this.http.post(`${this._apiUrl}`, data);
  }

  getContactList(input: GetContactListRequestDTO) {
    let params = new HttpParams();
    Object.entries(input).forEach(([key, value]) => {
      if (value) {
        params = params.append(key, value.toString());
      }
    });

    return this.http.get<GetContactListResponseDTO>(`${this._apiUrl}/lista-contatos-gerados`, { params })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error); // Retorna o erro original
        })
      );
  }

  deleteContactList(id: string) {
    return this.http.delete(`${this._apiUrl}/${id}`);
  }

  getDetailedList(input: GetDetailedListRequestDTO) {
    const { identificadorLista, ...filters } = input;
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params = params.append(key, value.toString());
      }
    });

    return this.http.get<{ paginacao: PaginationDto } & DetailedListModel>(
      `${this._apiUrl}/lista/${identificadorLista}`,
      {
        params,
      },
    );
  }

  getDetailedListClient(detailedListClientId: string) {
    return this.http.get<DetailedListClient>(
      `${this._apiUrl}/${detailedListClientId}`,
    );
  }
  saveDetailedListClientData(data: {
    id: string;
    statusClienteId: string;
    mensagem: string;
    dataAgendamento: string;
    qualificarContato: boolean;
    removerDaLista: boolean;
    statusAtualProjeto?: number | null;
    projetoCriado?: boolean;
  }) {
    const { id, ...input } = data;
    return this.http.post(`${this._apiUrl}/${id}`, input);
  }

  getQualifiedContactList(input: GetQualifiedContactListRequestDTO) {

    const { ...filters } = input;
    let params = new HttpParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params = params.append(key, value.toString());
      }
    });

    return this.http.get<GetQualifiedContactListResponseDTO>(
      `${this._apiUrl}/lista/qualificados`,
      { params }
    );
  }

  incluirContatoEmLista(data: {
    clienteId: string;
    feiraId: string;
    listaDeContatosGeradosId: string;
  }) {
    return this.http.post(`${this._apiUrl}/incluir-contato-em-lista`, data);
  }

  arquivarLista(listContatoGeradoId: string) {
    return this.http.put(`${environment.apiUrl}/v1/ListaDeContatosGerados/arquivar-lista`, {
      listContatoGeradoId
    });
  }

  contarEmailsValidos(listaContatoGeradoId: string) {
    return this.http.get<CountValidEmailsResponseDTO>(
      `${this._apiUrl}/contar-emails-validos/${listaContatoGeradoId}`
    );
  }
}